import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon } from 'lucide-react';

import FileDollarIcon from '@/components/atoms/Icons/FileDollarIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { formatPrice } from '@/modules/billing/utils';
import { COLUMN_PRESETS } from '@/util/constants';
import { addDaysToDate, formatDate, getEndOfMonth, isSameDay } from '@/util/dateUtils';

export interface InvoiceWithCalculatedValues {
  period_start: number;
  period_end: number;
  paid: boolean;
  total: number;
  currency: string;
  hosted_invoice_url?: string;
  calcValues: { dateFrom: Date; dateTo: Date; year: string };
}

export const columns = (): ColumnDef<InvoiceWithCalculatedValues>[] => {
  return [
    {
      accessorFn: (row) => row.period_start,
      id: 'period',
      ...COLUMN_PRESETS.FLEX,
      meta: { preset: 'FLEX', className: 'pl-6' },
      header: ({ column }) => (
        <SortableHeader column={column}>{t('billing.invoices.period')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { dateFrom, dateTo } = row.original.calcValues;
        if (!isSameDay(dateFrom, dateTo)) {
          return (
            <div className="truncate">
              {formatDate(dateFrom, 'MMM d')} — {formatDate(addDaysToDate(dateTo, -1), 'MMM d')}
            </div>
          );
        }
        return (
          <div className="truncate">
            {formatDate(dateFrom, 'MMM d')} —{' '}
            {formatDate(getEndOfMonth(new Date(row.original.period_end * 1000)), 'MMM d')}
          </div>
        );
      },
    },
    {
      accessorKey: 'paid',
      header: t('invoices.payment_status'),
      enableSorting: false,
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
      cell: ({ row }) => {
        return (
          <Badge type="secondary" flavour={row.original.paid ? 'successful' : 'destructive'}>
            {row.original.paid ? t('invoices.paid') : t('invoices.failed')}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'total',
      header: t('invoices.amount'),
      enableSorting: false,
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', isNumeric: true },
      cell: ({ row }) => {
        return <span>{formatPrice(row.original.total, row.original.currency)}</span>;
      },
    },
    {
      accessorKey: 'hosted_invoice_url',
      header: '',
      enableSorting: false,
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
      cell: ({ row }) => {
        const invoice = row.original;

        if (!invoice.hosted_invoice_url) {
          return null;
        }

        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost-neutral" size="icon-sm">
                <EllipsisVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  window.open(invoice.hosted_invoice_url, '_blank', 'noopener,noreferrer')
                }
                className="flex items-center gap-2"
              >
                <FileDollarIcon size={16} className="text-foreground-muted" />
                {t('invoices.view_invoice')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};
